import { useEffect, useMemo, useState } from "react";
import { useLocation, useParams } from "react-router-dom";
import dayjs from "dayjs";
import "dayjs/locale/id";
import { useQuery } from "@tanstack/react-query";
import { getPublicCertificate } from "../../../api/certificate";
import {
  CertificateConfig,
  DEFAULT_CERTIFICATE_TEMPLATE,
  DEFAULT_CERTIFICATE_CBT_TEMPLATE,
  getCertificateAsBase64,
} from "../../../utils/certificateGenerator";
import { buildAutoCertificateConfig } from "../../../utils/helpers";
import {
  normalizeLeadingInteger,
  normalizeMaterialJpl,
} from "../../../utils/numberNormalizer";
import { all_routes } from "../../router/all_routes";

const romanMonths = [
  "I",
  "II",
  "III",
  "IV",
  "V",
  "VI",
  "VII",
  "VIII",
  "IX",
  "X",
  "XI",
  "XII",
];

const buildCertificateNumber = (sequence: string, isoDate: string) => {
  const date = dayjs(isoDate);
  const monthRoman = romanMonths[date.month()] || "I";
  const year = date.year();
  const padded = sequence.padStart(3, "0");
  return `${padded}/JP-SF/E/${monthRoman}/${year}`;
};

const normalizeCertificateNumber = (certificateNumber?: string | null) => {
  if (!certificateNumber) return "";

  const parts = certificateNumber.trim().split("/");

  if (parts.length === 4 && parts[1]?.toUpperCase() === "JP-SF") {
    const [sequence, prefix, month, year] = parts;
    return `${sequence}/${prefix}/E/${month}/${year}`;
  }

  if (parts.length === 5 && parts[1]?.toUpperCase() === "JP-SF") {
    return certificateNumber.trim();
  }

  return certificateNumber.trim();
};

const createVerificationHash = (value: string) => {
  let hash = 0;

  for (let index = 0; index < value.length; index += 1) {
    hash = (hash << 5) - hash + value.charCodeAt(index);
    hash |= 0;
  }

  return Math.abs(hash).toString(16).padStart(12, "0");
};

const formatSignedDate = (dateInput?: string) => {
  if (!dateInput) return "-";

  const date = dayjs(dateInput);
  if (!date.isValid()) return dateInput;

  return date.locale("id").format("dddd, D MMMM YYYY [pukul] HH:mm");
};

const parseScoreValue = (value?: string | number | null) => {
  if (value === null || value === undefined || value === "") return NaN;
  return Number(String(value).replace("%", "").replace(",", "."));
};

const normalizePreviewMaterials = (
  materials: unknown,
  studied: unknown,
  fallbackSpeaker = "",
): CertificateConfig["materials"] => {
  let rows: any[] = [];

  if (Array.isArray(materials)) {
    rows = materials;
  } else if (typeof materials === "string") {
    try {
      const parsed = JSON.parse(materials);
      rows = Array.isArray(parsed)
        ? parsed
        : parsed && typeof parsed === "object"
        ? Object.values(parsed)
        : [];
    } catch {
      rows = materials
        .split(",")
        .map((topic) => ({ topic: topic.trim() }))
        .filter((item) => item.topic);
    }
  } else if (materials && typeof materials === "object") {
    rows = Object.values(materials);
  }

  if (!rows.length) {
    if (Array.isArray(studied)) {
      rows = studied;
    } else if (typeof studied === "string") {
      try {
        const parsed = JSON.parse(studied);
        rows = Array.isArray(parsed) ? parsed : [];
      } catch {
        rows = studied
          .split(",")
          .map((topic) => topic.trim())
          .filter(Boolean);
      }
    }
  }

  return rows
    .map((item: any) => ({
      topic:
        typeof item === "string"
          ? item
          : item?.topic || item?.name || item?.title || "",
      jpl:
        typeof item === "string"
          ? ""
          : normalizeMaterialJpl(item?.jpl || item?.duration || ""),
      speaker:
        typeof item === "string"
          ? fallbackSpeaker
          : item?.speaker || fallbackSpeaker,
    }))
    .filter((item) => item.topic || item.jpl || item.speaker);
};

const PublicCertificate = () => {
  const { certificateNumber } = useParams<{ certificateNumber: string }>();
  const { search } = useLocation();
  const queryNumber = new URLSearchParams(search).get("number") || "";
  const resolvedCertificateNumber = certificateNumber || queryNumber;
  const [pdfUrl, setPdfUrl] = useState<string>("");

  const { data, isLoading, isError } = useQuery({
    queryKey: ["public-certificate", resolvedCertificateNumber],
    queryFn: () => getPublicCertificate(resolvedCertificateNumber),
    enabled: Boolean(resolvedCertificateNumber),
  });

  const certificateData = data?.data;

  const signatureDate = useMemo(() => {
    if (!certificateData) return "";
    const course = certificateData.course;
    const cert = certificateData.certificate;

    if (course?.type === "Live_Teaching") {
      return course?.end_date || "";
    }

    return cert?.created_at || "";
  }, [certificateData]);

  const autoCertificateNumber = useMemo(() => {
    const cert = certificateData?.certificate;
    if (cert?.certificate_number) {
      return normalizeCertificateNumber(cert.certificate_number);
    }
    if (!cert?.id || !signatureDate) return "";
    return buildCertificateNumber(String(cert.id), signatureDate);
  }, [certificateData, signatureDate]);

  const effectiveCertificateNumber = useMemo(() => {
    const cert = certificateData?.certificate;
    return (
      normalizeCertificateNumber(cert?.certificate_number) ||
      autoCertificateNumber
    );
  }, [certificateData, autoCertificateNumber]);

  const verificationHash = useMemo(() => {
    const cert = certificateData?.certificate;
    const course = certificateData?.course;
    return createVerificationHash(
      [
        effectiveCertificateNumber,
        cert?.name || "",
        course?.title || "",
        cert?.created_at || "",
      ].join("|"),
    );
  }, [certificateData, effectiveCertificateNumber]);

  const qrValue = useMemo(() => {
    const cert = certificateData?.certificate;
    if (effectiveCertificateNumber) {
      return `${window.location.origin}${
        all_routes.verifyCertificate
      }?number=${encodeURIComponent(effectiveCertificateNumber)}`;
    }
    return cert?.qr_url || undefined;
  }, [certificateData, effectiveCertificateNumber]);

  const evaluationScoreValue = useMemo(() => {
    const cert = certificateData?.certificate;
    return cert?.evaluation_score ?? "";
  }, [certificateData]);

  const evaluationPassingGradeValue = useMemo(() => {
    const course = certificateData?.course;
    const cert = certificateData?.certificate;
    return normalizeLeadingInteger(
      cert?.evaluation_passing_grade ?? course?.min_passing_score ?? "",
    );
  }, [certificateData]);

  const cbtEnabled = Boolean(
    evaluationScoreValue || evaluationPassingGradeValue,
  );

  const evaluationPassed = useMemo(() => {
    if (!cbtEnabled) return undefined;
    const cert = certificateData?.certificate;
    if (cert?.evaluation_passed === true) return true;
    if (cert?.evaluation_passed === false) return false;

    const score = parseScoreValue(evaluationScoreValue);
    const passing = parseScoreValue(evaluationPassingGradeValue);
    if (!Number.isFinite(score) || !Number.isFinite(passing)) return undefined;
    return score >= passing;
  }, [
    cbtEnabled,
    certificateData,
    evaluationScoreValue,
    evaluationPassingGradeValue,
  ]);

  useEffect(() => {
    const generatePreview = async () => {
      if (!certificateData?.course || !certificateData?.certificate) return;

      const course = certificateData.course;
      const cert = certificateData.certificate;
      const hasLiveTeachingCbt =
        course.type === "Live_Teaching" &&
        Number(course.questions_count || 0) > 0;
      const materialTopicOnly =
        course.type === "Live_Teaching"
          ? !hasLiveTeachingCbt
          : course.type !== "CBT";

      const materials = normalizePreviewMaterials(
        course.certificate_materials,
        course.studied,
        course.certificate_speakers || "",
      );

      const config: CertificateConfig = buildAutoCertificateConfig(
        cert.name,
        course.title,
        {
          materials,
          materialTopicOnly,
          certificateNumber: effectiveCertificateNumber || undefined,
          qrValue: qrValue || undefined,
          eventDates: course.certificate_event_dates || undefined,
          jplTotal: course.certificate_jpl_total || undefined,
          jplNote: course.certificate_jpl_note || undefined,
          speakers: course.certificate_speakers || undefined,
          evaluationScore: cbtEnabled
            ? String(evaluationScoreValue || "")
            : undefined,
          evaluationPassed,
          evaluationPassingGrade: cbtEnabled
            ? String(evaluationPassingGradeValue || "")
            : undefined,
          signature: {
            location: course.certificate_location || "Bandar Lampung",
            date: signatureDate || undefined,
          },
        },
      );

      const template =
        course.type === "CBT"
          ? DEFAULT_CERTIFICATE_CBT_TEMPLATE
          : DEFAULT_CERTIFICATE_TEMPLATE;

      const previewUrl = await getCertificateAsBase64(config, template);
      setPdfUrl(previewUrl);
    };

    generatePreview();
  }, [
    certificateData,
    cbtEnabled,
    effectiveCertificateNumber,
    evaluationPassed,
    evaluationPassingGradeValue,
    evaluationScoreValue,
    qrValue,
    signatureDate,
  ]);

  if (isLoading) {
    return (
      <div className="content">
        <div className="container">
          <div
            className="d-flex justify-content-center align-items-center"
            style={{ minHeight: 400 }}
          >
            <div className="spinner-border text-primary" role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (isError || !certificateData?.course || !certificateData?.certificate) {
    return (
      <div className="content">
        <div className="container">
          <div className="alert alert-danger mt-4">
            Sertifikat tidak ditemukan.
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="content">
      <div className="container">
        <div className="card mb-4">
          <div className="card-body">
            <div className="certificate-verification mx-auto">
              <style>
                {`
                  .certificate-verification {
                    max-width: 720px;
                    color: #2b2f38;
                  }

                  .certificate-verification .verification-title {
                    text-align: center;
                    font-size: 20px;
                    line-height: 1.55;
                    font-weight: 800;
                    letter-spacing: 0.2px;
                    text-transform: uppercase;
                    margin-bottom: 24px;
                  }

                  .certificate-verification .verification-line {
                    border-top: 1px solid #e5e7eb;
                    margin: 18px 0;
                  }

                  .certificate-verification .verification-row {
                    margin-bottom: 14px;
                    font-size: 15px;
                    line-height: 1.55;
                  }

                  .certificate-verification .verification-label {
                    color: #5f6673;
                  }

                  .certificate-verification .verification-value {
                    font-weight: 700;
                    color: #20242c;
                  }

                  .certificate-verification .hash-value {
                    color: #dc3545;
                    font-weight: 600;
                    word-break: break-all;
                  }
                `}
              </style>

              <div className="verification-title">
                SERTIFIKAT INI TERVERIFIKASI 
                <br />
                SISTEM E-SIGN JADIPRAKTISI
              </div>
              <div className="verification-row">
                <span className="verification-label">
                  Sertifikat ini telah terverifikasi oleh:
                </span>
              </div>

              <div className="verification-line" />

              <div className="verification-row">
                <span className="verification-label">Nama : </span>
                <span className="verification-value">
                  apt. Rudi Afrinanda, S.Farm
                </span>
              </div>
              <div className="verification-row">
                <span className="verification-label">Jabatan : </span>
                <span className="verification-value">
                 Direktur PT. Jadi Praktisi Indonesia
                </span>
              </div>
              <div className="verification-row">
                <span className="verification-label">Unit Kerja : </span>
                <span className="verification-value">
                 Direksi
                </span>
              </div>
              <div className="verification-row">
                <span className="verification-label">Instansi : </span>
                <span className="verification-value">PT. Jadi Praktisi Indonesia</span>
              </div>

              <div className="verification-line" />

              <div className="verification-row">
                <span className="verification-label">Nomor Sertifikat : </span>
                <span className="verification-value">
                  {effectiveCertificateNumber}
                </span>
              </div>
              <div className="verification-row">
                <span className="verification-label">Nama Peserta : </span>
                <span className="verification-value">
                  {certificateData.certificate.name}
                </span>
              </div>
              <div className="verification-row mb-0">
                <span className="verification-label">Kelas : </span>
                <span className="verification-value">
                  {certificateData.course.title}
                </span>
              </div>
            </div>
          </div>
        </div>

        <div className="card">
  <div className="card-body text-center">
    <h4 className="mb-3">Sertifikat Peserta</h4>

    {pdfUrl ? (
      <a
        href={pdfUrl}
        download="sertifikat-peserta.pdf"
        target="_blank"
        rel="noopener noreferrer"
        className="btn btn-primary"
      >
        Download PDF Sertifikat
      </a>
    ) : (
      <div className="text-muted">Menyiapkan sertifikat...</div>
    )}
  </div>
</div>
      </div>
    </div>
  );
};

export default PublicCertificate;
